// @ts-nocheck
import React, { useState } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  Printer,
  AlertOctagon,
  PackageCheck,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import PrintDialog from "@/components/shared/PrintDialog";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { trpcClient } from "@/lib/trpc";
import { formatDate, formatNumber } from "@/lib/utils";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { getStatusConfig } from "@/config/status-registry";
import { EntityLink } from "@/components/shared/EntityLink";
import DetailNotFoundState from "@/components/shared/DetailNotFoundState";
import {
  StageDefectSelectorTable,
  normalizeDefectTallyItems,
} from "@/components/quality/StageDefectSelectorTable";
import {
  CriteriaTable,
  LegacyMeasurements,
  QcSamplingSummary,
  VerdictSummary,
} from "@/components/quality/qc-form";
import {
  QcCategorizedMediaSection,
  normalizeCategorizedAttachments,
} from "@/components/quality/QcCategorizedMediaSection";
import { DetailField as F } from "@/components/shared/DetailField";


export default function QCMaterialDetailPage() {
  const navigate = useNavigate();
  const { checkId } = useParams({ strict: false }) as { checkId: string };
  const [showDelete, setShowDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showPrint, setShowPrint] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await (trpcClient as any).qcMaterials.delete.mutate({ id: checkId });
      navigate({ to: "/qc-materials" });
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi xóa");
    } finally {
      setIsDeleting(false);
      setShowDelete(false);
    }
  };

  const {
    data: qc,
    isLoading,
    error,
    refetch: refetchDetail,
  } = useQuery({
    queryKey: ["qcMaterial", checkId],
    queryFn: async () =>
      (trpcClient as any).qcMaterials.getById.query({ id: checkId }),
  });

  const d = qc as any;

  if (!isLoading && !d) {
    return (
      <DetailNotFoundState
        breadcrumbs={[{ label: "QC Vật tư", to: "/qc-materials" }]}
        entityLabel="Phiếu QC vật tư"
        backTo="/qc-materials"
        codeOrId={checkId}
        error={error}
        onRetry={() => refetchDetail()}
      />
    );
  }
  if (isLoading || !d) {
    return (
      <StandardDetailLayout
        breadcrumbs={[{ label: "QC Vật tư", to: "/qc-materials" }]}
        title="Đang tải..."
      >
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
        </div>
      </StandardDetailLayout>
    );
  }

  if (error) {
    return (
      <StandardDetailLayout
        breadcrumbs={[{ label: "QC Vật tư", to: "/qc-materials" }]}
        title="Lỗi"
      >
        <div className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-rose-500 mx-auto mb-4" />
          <Button onClick={() => navigate({ to: "/qc-materials" })}>
            Quay lại
          </Button>
        </div>
      </StandardDetailLayout>
    );
  }

  const sts = getStatusConfig("QCStatus", d.status);
  const isNonConforming =
    d.status === "FAILED" || d.status === "WARNING" || d.status === "fail" || d.status === "warning";

  // Parse checklist JSON
  let checklistItems: any[] = [];
  try {
    if (d.checklist) {
      const parsed = typeof d.checklist === "string" ? JSON.parse(d.checklist) : d.checklist;
      checklistItems = Array.isArray(parsed)
        ? parsed
        : Array.isArray(parsed?.items)
          ? parsed.items
          : [];
    }
  } catch {
    /* ignore */
  }

  const handleCreateNcrLinkToForm = () => {
    navigate({
      to: "/ncr/create",
      search: {
        sourceType: "IQC",
        sourceId: d.id,
      } as any,
    });
  };

  return (
    <>
      <StandardDetailLayout
        breadcrumbs={[{ label: "Kiểm tra Vật tư (IQC)", to: "/qc-materials" }]}
        title={d.materialQcId}
        entityId={d.id}
        entityType="qc_material"
        timestamps={{ created: d.createdAt, updated: d.updatedAt }}
        status={
          <Badge variant="outline" className={sts.color}>
            {sts.label || d.status}
          </Badge>
        }
        actions={
          <div className="flex gap-1.5 flex-wrap">
            {isNonConforming && (
              <Button
                variant="default"
                size="sm"
                className="bg-rose-600 hover:bg-rose-700 text-white font-semibold"
                onClick={handleCreateNcrLinkToForm}
                title="Lập phiếu NCR xử lý lỗi từ biên bản IQC này"
              >
                <AlertOctagon size={14} className="mr-1.5" /> Tạo NCR
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="gap-1 text-indigo-700 border-indigo-200 hover:bg-indigo-50 font-semibold"
              onClick={() => setShowPrint(true)}
              title="In phiếu IQC (BM-QC-01)"
            >
              <Printer size={14} className="mr-1" /> In phiếu
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() =>
                navigate({
                  to: "/qc-materials/$checkId/edit",
                  params: { checkId },
                })
              }
            >
              <Pencil size={14} className="mr-1.5" /> Sửa
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-rose-600 border-rose-200 hover:bg-rose-50"
              onClick={() => setShowDelete(true)}
            >
              <Trash2 size={14} className="mr-1.5" /> Xoá
            </Button>
          </div>
        }
        onBack={() => navigate({ to: "/qc-materials" })}
      >
        <div className="space-y-4">
          {/* ISO 9001 §8.7 Non-Conformance Alert Banner */}
          {isNonConforming && (
            <div className="rounded-xl border border-rose-300 bg-rose-50/90 p-3.5 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-start gap-2.5">
                <AlertOctagon className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs font-bold text-rose-900 uppercase">
                    Cảnh Báo Sự Cố Chất Lượng Đầu Vào (ISO 9001:2015 §8.7)
                  </div>
                  <div className="text-xs text-rose-700 mt-0.5">
                    Lô vật tư <strong>{d.batchNumber}</strong> có kết quả kiểm tra{" "}
                    <strong>{sts.label || d.status}</strong> (SL lỗi/từ chối:{" "}
                    <strong>
                      {formatNumber(d.rejectedQty || 0)} {d.unit || "Tờ"}
                    </strong>
                    ). Vui lòng lập Báo cáo Không phù hợp (NCR) để cách ly lô hàng hoặc yêu cầu NCC đổi trả.
                  </div>
                </div>
              </div>
              <Button
                size="sm"
                className="bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold shrink-0"
                onClick={handleCreateNcrLinkToForm}
              >
                <AlertOctagon className="w-3.5 h-3.5 mr-1.5" />
                Tạo NCR
              </Button>
            </div>
          )}

          {/* Thông tin chung + Lấy mẫu */}
          <div className="border rounded-xl bg-white shadow-2xs p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2">
              <div className="flex items-center gap-2">
                <PackageCheck className="w-4 h-4 text-indigo-600" />
                <span className="text-xs font-bold uppercase tracking-wider text-slate-800">Thông tin chung</span>
              </div>
              {d.template && (
                <Badge variant="outline" className="font-mono text-[11px] bg-indigo-50 text-indigo-700 border-indigo-200">
                  Mẫu kiểm tra: [{d.template.code}] {d.template.name}
                </Badge>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-2.5">
              <F
                label="Vật tư"
                value={
                  <EntityLink
                    entityType="material"
                    id={d.material?.id || d.materialId}
                    code={d.material?.materialId}
                    label={d.material?.name}
                    variant="badge"
                  />
                }
              />
              <F label="Nhóm vật tư" value={d.materialCategory || "PAPER"} mono />
              <F
                label="Nhà cung cấp"
                value={
                  d.vendor ? (
                    <EntityLink entityType="vendor" id={d.vendor.id} code={d.vendor.code} label={d.vendor.name} variant="badge" />
                  ) : (
                    d.supplierName || "—"
                  )
                }
              />
              <F
                label="Đơn mua"
                value={
                  d.purchaseOrder ? (
                    <EntityLink
                      entityType="purchaseOrder"
                      id={d.purchaseOrder.id}
                      code={d.purchaseOrder.poId}
                      label={d.purchaseOrder.poId}
                      variant="badge"
                    />
                  ) : (
                    d.purchaseOrderId || "—"
                  )
                }
              />
              <F label="Số lô NCC" value={d.batchNumber} mono />
              <F
                label="Số CoA"
                value={`${d.coaNumber || "—"}${d.coaVerified === false ? "" : " · đã đối chiếu"}`}
                mono
              />
              <F label="Ngày kiểm" value={formatDate(d.checkTime)} />
              <F label="Người kiểm" value={d.inspector?.fullName || d.inspector?.email || "Chưa phân công"} />
            </div>

            <div className="border-t border-slate-100 pt-3">
              <QcSamplingSummary
                lotSize={d.lotSize ?? d.quantity}
                unit={d.unit}
                mode={d.inspectionMode}
                level={d.aqlLevel}
                aqlMajor={d.aqlMajor}
                aqlMinor={d.inspectionMode ? d.aqlMinor : null}
                sampleSize={d.sampleSize}
                acMajor={d.acMajor}
                reMajor={d.reMajor}
                acMinor={d.acMinor}
                reMinor={d.reMinor}
              />
            </div>
          </div>

          {/* Chỉ tiêu kiểm tra (bản chụp trên phiếu) */}
          <CriteriaTable items={checklistItems} readOnly emptyText="Phiếu chưa ghi chỉ tiêu." />

          {/* Phiếu lập trước đợt 2: số đo ô cứng cũ */}
          {!d.inspectionMode && (
            <LegacyMeasurements
              rows={[
                ["Định lượng", d.actualGsm ? `${d.actualGsm} g/m²` : null],
                ["Độ dày", d.actualCaliper ? `${d.actualCaliper} mm` : null],
                ["Độ ẩm", d.actualMoisture ? `${d.actualMoisture} %` : null],
                ["Khổ × dài", d.actualWidth || d.actualLength ? `${d.actualWidth || "—"} × ${d.actualLength || "—"} mm` : null],
                ["Sớ giấy", d.grainDirection === "LONG_GRAIN" ? "Sớ dọc" : d.grainDirection === "SHORT_GRAIN" ? "Sớ ngang" : null],
                ["Cobb60", d.cobbValue ? `${d.cobbValue} g/m²` : null],
              ]}
            />
          )}

          {/* Lỗi phát hiện */}
          {normalizeDefectTallyItems(d.defectDetails).length > 0 && (
            <StageDefectSelectorTable
              processStage="IQC"
              lotSize={d.quantity || 0}
              sampleSize={d.sampleSize || 0}
              defectDetails={normalizeDefectTallyItems(d.defectDetails)}
              onChange={() => {}}
              readOnly
              hideSampleSummary
            />
          )}

          {/* Kết luận */}
          <VerdictSummary
            status={d.status}
            counts={{ critical: d.criticalDefects || 0, major: d.majorDefects || 0, minor: d.minorDefects || 0 }}
            sampleSize={d.sampleSize}
            reMajor={d.reMajor}
            reMinor={d.reMinor}
            overrideReason={d.overrideReason}
            criteria={checklistItems}
          >
            <div className="grid grid-cols-1 gap-x-6 gap-y-2 border-t border-slate-100 pt-2 sm:grid-cols-3">
              <F
                label="SL nhập kho"
                value={
                  <span className="text-emerald-700 font-bold font-mono">
                    {formatNumber(d.acceptedQty ?? d.quantity ?? 0)} {d.unit || "Tờ"}
                  </span>
                }
              />
              <F
                label="SL loại"
                value={
                  <span className="text-rose-600 font-bold font-mono">
                    {formatNumber(d.rejectedQty || 0)} {d.unit || "Tờ"}
                  </span>
                }
              />
              <F label="Nhận xét" value={d.notes || "—"} />
            </div>
          </VerdictSummary>

          {/* Ảnh mẫu / lỗi / CoA */}
          {normalizeCategorizedAttachments(d.attachments, "QC").length > 0 && (
            <QcCategorizedMediaSection mode="QC" value={d.attachments} onChange={() => {}} readOnly />
          )}

          {/* Card 4: Linked Non-Conformance Reports (NCRs) */}
          {Array.isArray(d.ncrs) && d.ncrs.length > 0 && (
            <div className="border border-rose-200 rounded-xl bg-white shadow-2xs overflow-hidden">
              <div className="px-4 py-2.5 border-b border-rose-100 bg-rose-50/60 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertOctagon className="w-4 h-4 text-rose-600" />
                  <span className="text-xs font-bold uppercase tracking-wider text-rose-900">
                    Phiếu NCR liên kết ({d.ncrs.length})
                  </span>
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                {d.ncrs.map((ncr: any) => (
                  <div
                    key={ncr.id}
                    className="px-4 py-2.5 flex items-center justify-between hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate({ to: `/ncr/${ncr.id}` as any })}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-bold text-rose-700 text-xs">{ncr.ncrId}</span>
                      <span className="text-xs font-medium text-slate-800">{ncr.issueSummary}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {ncr.disposition && (
                        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-200">
                          {ncr.disposition}
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-[10px] font-mono">
                        {ncr.status}
                      </Badge>
                      <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </StandardDetailLayout>

      <ConfirmDialog
        open={showDelete}
        onOpenChange={setShowDelete}
        title={`Xoá QC vật tư ${d.materialQcId}?`}
        description="Hành động này không thể hoàn tác."
        onConfirm={handleDelete}
        isLoading={isDeleting}
        variant="destructive"
      />

      <PrintDialog
        open={showPrint}
        onClose={() => setShowPrint(false)}
        type="qc_material"
        data={d}
      />
    </>
  );
}
